import { useEffect, useRef } from 'react';
import { debounce } from 'lodash-es';

import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import * as s from './styles';
import Hero from './components/organisms/hero';
import Products from './components/organisms/products';
import cardData from './components/organisms/products/static/data';
import Features from './components/organisms/features';
import FullColumnImageCta from './components/organisms/fullColumnImageCta';
import FullColumnTextCta  from './components/organisms/fullColumnTextCta';
import Social from './components/organisms/social';
import Audience from './components/organisms/audience';
import type { IMonoHomepage } from './types';

const MonoHomepage = ({
  audience,
  features,
  fullColumnImageCta,
  fullColumnTextCta,
  homepageHero,
  includeSections,
  logosOrganism,
  products,
  social,
  variation = 'control',
}: IMonoHomepage ) => {
  
  const {
    includeAudience,
    includeFeatures,
    includeFullColumnImageCta,
    includeFullColumnTextCta,
    includeLogos,
    includeProducts,
    includeSocial,
  } = includeSections;

  const ref = useRef(0);
  const prevRef = useRef(0);
  useEffect( () => {
    const logScrollPosition = (milestone: number) => {
      if (ref.current !== milestone) {
        ref.current = milestone;
        gtmBasicEvent({ action: `${milestone}%`, category: 'scroll', label: 'undefined', non_interaction_flag: true });
      }
    };

    const handleScrollPosition = debounce(() =>  {
      const scrollingElement = document.documentElement;
      const scrollPercentage = Math.floor((scrollingElement.scrollTop / (scrollingElement.scrollHeight - scrollingElement.clientHeight) * 100));
      ref.current = ref.current || 0;

      if (scrollPercentage >= 25 && scrollPercentage < 50 && prevRef.current < 25) {
        prevRef.current = ref.current;
        logScrollPosition(25);
      }
      if (scrollPercentage >= 50 && scrollPercentage < 75 && prevRef.current < 50) {
        prevRef.current = ref.current;
        logScrollPosition(50);
      }
      if (scrollPercentage >= 75 && scrollPercentage < 100 && prevRef.current < 75) {
        prevRef.current = ref.current;
        logScrollPosition(75);
      }
      if (scrollPercentage === 100 && prevRef.current < 100) {
        prevRef.current = ref.current;
        logScrollPosition(100);
      }
    }, 30);
    
    handleScrollPosition();
    
    window.addEventListener('scroll', handleScrollPosition);

    return () => {
      window.removeEventListener('scroll', handleScrollPosition);
    };
  }, []);
  
  return (
    <div>
      <s.HeroWrapper>
        <Hero heroContent={homepageHero.heroContent} ctaContent={homepageHero.ctaContent}>
          {variation === 'logoPlacement' && includeLogos && (
            <s.HomepageLogos
              {...logosOrganism}
            />
          )}
        </Hero>
        <s.LightSandWrapper>
          {includeProducts && <Products
            gradientHeadline={products.gradientHeadline}
            productVersion={products.productVersion}
            cards={cardData}
          />}
          {includeFeatures && <Features
            buttons={features.buttons}
            gradientHeadline={features.gradientHeadline}
            featureCards={features.featureCards}
          />}
        </s.LightSandWrapper>
      </s.HeroWrapper>
      <s.GradientBackgroundsWrapper>
        {includeFullColumnImageCta && <FullColumnImageCta
          buttons={fullColumnImageCta.buttons}
          gradientHeadline={fullColumnImageCta.gradientHeadline}
          image={fullColumnImageCta.image}
        />}
        {includeSocial && <Social
          gradientHeadline={social.gradientHeadline}
          socialCards={social.socialCards}
          buttons={social.buttons}
        />}
        {variation === 'control' && includeLogos && <s.HomepageLogos
          {...logosOrganism}
        />}
    </s.GradientBackgroundsWrapper>
    <s.AudienceFinalCtaWrapper>
      {includeAudience && <Audience 
        headline={audience.headline}
        audienceCards={audience.audienceCards}
      />}
      {includeFullColumnTextCta && <FullColumnTextCta  
        headline={fullColumnTextCta?.headline}
        buttons={fullColumnTextCta?.buttons}
      />}
    </s.AudienceFinalCtaWrapper>
  </div>
  );
};

export default MonoHomepage;
